import { useCallback, useEffect, useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Settings, Check, Loader2 } from "lucide-react";
import { useToast } from "../../../components/ui/Toast";

// ── Queries ──────────────────────────────────────────────────────

const GET_TENANT_FEATURE_CONFIG = gql`
  query TenantFeatureConfigForBlog {
    currentTenant {
      id
      featureConfig
    }
  }
`;

const UPDATE_TENANT_CONFIG = gql`
  mutation UpdateTenantConfigForBlog($input: TenantConfigInput!) {
    updateTenantConfig(input: $input) {
      id
      featureConfig
    }
  }
`;

// ── Types ────────────────────────────────────────────────────────

interface ArticleConfig {
  breadcrumbLabel?: string;
  showByline?: boolean;
  showPrevNext?: boolean;
  articleSection?: string;
}

interface FeatureConfig {
  [key: string]: unknown;
  blog?: {
    enabled?: boolean;
    path?: string;
    article?: ArticleConfig;
    [key: string]: unknown;
  };
}

function parseFeatureConfig(raw: string | null | undefined): FeatureConfig {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as FeatureConfig;
  } catch {
    return {};
  }
}

// ── Component ────────────────────────────────────────────────────

export function BlogSettingsPanel() {
  const toast = useToast();
  const { data, loading: queryLoading } = useQuery<{
    currentTenant: { id: string; featureConfig: string | null } | null;
  }>(GET_TENANT_FEATURE_CONFIG);

  const [updateConfig, { loading: saving }] = useMutation(UPDATE_TENANT_CONFIG);

  const featureConfig = useMemo(
    () => parseFeatureConfig(data?.currentTenant?.featureConfig),
    [data?.currentTenant?.featureConfig],
  );

  const existingArticle = featureConfig.blog?.article || {};

  const [breadcrumbLabel, setBreadcrumbLabel] = useState("");
  const [showByline, setShowByline] = useState(true);
  const [showPrevNext, setShowPrevNext] = useState(true);
  const [articleSection, setArticleSection] = useState("");

  // Sync form state from server data
  useEffect(() => {
    setBreadcrumbLabel(existingArticle.breadcrumbLabel || "");
    setShowByline(existingArticle.showByline !== false);
    setShowPrevNext(existingArticle.showPrevNext !== false);
    setArticleSection(existingArticle.articleSection || "");
  }, [
    existingArticle.breadcrumbLabel,
    existingArticle.showByline,
    existingArticle.showPrevNext,
    existingArticle.articleSection,
  ]);

  const isDirty = useMemo(() => {
    const orig = existingArticle;
    return (
      (breadcrumbLabel || "") !== (orig.breadcrumbLabel || "") ||
      showByline !== (orig.showByline !== false) ||
      showPrevNext !== (orig.showPrevNext !== false) ||
      (articleSection || "") !== (orig.articleSection || "")
    );
  }, [breadcrumbLabel, showByline, showPrevNext, articleSection, existingArticle]);

  const handleSave = useCallback(async () => {
    // Merge article config into existing feature_config
    const merged: FeatureConfig = {
      ...featureConfig,
      blog: {
        ...(featureConfig.blog || {}),
        article: {
          breadcrumbLabel: breadcrumbLabel || undefined,
          showByline,
          showPrevNext,
          articleSection: articleSection || undefined,
        },
      },
    };

    try {
      await updateConfig({
        variables: {
          input: {
            featureConfig: JSON.stringify(merged),
          },
        },
      });
      toast.push({ kind: "success", title: "Blog settings saved" });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Save failed";
      toast.push({ kind: "error", title: msg });
    }
  }, [
    featureConfig,
    breadcrumbLabel,
    showByline,
    showPrevNext,
    articleSection,
    updateConfig,
    toast,
  ]);

  if (queryLoading) {
    return (
      <div className="shadow-card rounded-lg bg-paper-hi p-5">
        <div className="flex items-center gap-2 mb-4">
          <Settings size={14} strokeWidth={1.8} className="text-ink-4" />
          <h3 className="text-[13px] font-semibold">Article settings</h3>
        </div>
        <div className="pulse-soft h-24 rounded bg-canvas-2" />
      </div>
    );
  }

  return (
    <div className="shadow-card rounded-lg bg-paper-hi p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Settings size={14} strokeWidth={1.8} className="text-ink-4" />
          <h3 className="text-[13px] font-semibold">Article settings</h3>
        </div>
        {isDirty && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-md bg-tide px-3 py-1.5 text-[12px] font-medium text-white hover:bg-tide-d transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Check size={12} strokeWidth={2.2} />
            )}
            Save
          </button>
        )}
      </div>

      <p className="text-[11.5px] text-ink-4 mb-4">
        Controls how individual blog articles appear on the public site.
      </p>

      <div className="space-y-4">
        {/* Breadcrumb label */}
        <div>
          <label className="block text-[12px] font-medium text-ink-3 mb-1">
            Breadcrumb label
          </label>
          <input
            type="text"
            value={breadcrumbLabel}
            onChange={(e) => setBreadcrumbLabel(e.target.value)}
            placeholder="Legal Insights"
            className="hair w-full rounded-md border bg-paper px-3 py-2 text-[13px] text-ink placeholder:text-ink-5 focus:outline-none focus:border-tide focus:ring-1 focus:ring-tide"
          />
          <p className="text-[11px] text-ink-5 mt-1">
            Shown in the breadcrumb above each article. Defaults to the blog path name.
          </p>
        </div>

        {/* Article section (meta tag) */}
        <div>
          <label className="block text-[12px] font-medium text-ink-3 mb-1">
            Article section
          </label>
          <input
            type="text"
            value={articleSection}
            onChange={(e) => setArticleSection(e.target.value)}
            placeholder="Legal"
            className="hair w-full rounded-md border bg-paper px-3 py-2 text-[13px] text-ink placeholder:text-ink-5 focus:outline-none focus:border-tide focus:ring-1 focus:ring-tide"
          />
          <p className="text-[11px] text-ink-5 mt-1">
            Used in the article:section meta tag for search engines.
          </p>
        </div>

        {/* Toggles */}
        <div className="space-y-3 pt-1">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={showByline}
              onChange={(e) => setShowByline(e.target.checked)}
              className="rounded border-ink-5 text-tide focus:ring-tide"
            />
            <span className="text-[12.5px] text-ink-2">
              Show author byline and date
            </span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={showPrevNext}
              onChange={(e) => setShowPrevNext(e.target.checked)}
              className="rounded border-ink-5 text-tide focus:ring-tide"
            />
            <span className="text-[12.5px] text-ink-2">
              Show previous / next navigation
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}
